import PageHeader from '@src/components/PageHeader/PageHeader';
import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import GeneralSettings from './General';
import BranchSettings from './BranchSettings';
import TeamsAndPermission from './TeamsAndPermission';
import Permission from './Permission';
import { useAppSelector } from '@src/store/hooks';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';
import { CustomAlertType } from '@src/interfaces/type';
import CustomAlert from '@src/components/CustomAlert/CustomAlert';
import { Alert, Snackbar } from '@mui/material';
import { RESTAURANT } from '@src/Constants';
import Integrations from './integrations';
import ApprovalList from './integrations/Tally/ApprovalList';
import SettingsMobileHomePage from './SettingsMobileHomePage';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	transition: width 0.3s ease;
`;

export type TeamView = 'team' | 'permission';
export type IntegrationsView = 'list' | 'manageTally';

const Settings = () => {
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const [isErrorVisible, setErrorVisible] = useState(false);
	const [alert, setAlert] = useState<CustomAlertType>({ message: '', severity: 'success' });
	const [errorMessage, setErrorMessage] = useState('');
	const [view, setView] = useState<TeamView>('team');
	const [integrationsView, setIntegrationsView] = useState<IntegrationsView>('list');
	const [collapse, setCollapse] = useState(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const [rows, setRows] = useState([]);
	const [filteredRows, setFilteredRows] = useState([]);

	const navigate = useNavigate();
	const location = useLocation();
	const { id, userId } = useParams();
	const { isDrawerOpen } = useAppSelector((state) => state?.UI);
	const isMobileWidth = IsMobileWidth();

	const pageContainerStyles: React.CSSProperties = {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		transition: 'width 0.3s ease',
		width:
			isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 240px)'
				: !isDrawerOpen && !isMobileWidth
				? 'calc(100vw - 64px)'
				: '100%',
	};

	const pageTitle = (path: string): string => {
		const page: string | undefined = path?.split('/')?.at(-1);
		const titles: Record<string, string> = {
			general: 'General Settings',
			branch:
				currentUser?.type?.toLowerCase() === RESTAURANT ? 'Dealer Settings' : 'Supplier Settings',
			teams: 'Teams & Permissions',
			integrations: 'Integrations',
			tallyApproval: 'Restaurant Approval List',
		};
		if (page && titles[page]) {
			return titles[page];
		}
		return 'Teams & Permissions';
	};

	const displayCustomError = (error: string) => {
		setErrorMessage(error);
		setErrorVisible(true);
	};

	const handleArrowBack = () => {
		if (view === 'permission') {
			setView('team');
			return;
		}
		if (integrationsView === 'manageTally') {
			setIntegrationsView('list');
			return;
		}
		navigate(-1);
	};

	const activeBackIcon = (path: string) => {
		return view === 'permission' || integrationsView === 'manageTally';
	};
	const handleErrorClose = () => {
		setErrorMessage('');
		setErrorVisible(false);
	};

	const settingsMobileRoutes = (
		path: string,
		id: string | undefined,
		userId: string | undefined
	) => {
		return (
			<SettingsMobileHomePage
				setSearchValue={setSearchValue}
				searchValue={searchValue}
				collapse={collapse}
				setCollapse={setCollapse}
				setAlert={setAlert}
				displayCustomError={displayCustomError}
				view={view}
				setView={setView}
				integrationsView={integrationsView}
				setIntegrationsView={setIntegrationsView}
			/>
		);
	};
	const settingsRoutes = (path: string, id: string | undefined, userId: string | undefined) => {
		switch (path) {
			case `/restaurants/${id}/settings/general`:
			case `/suppliers/${id}/settings/general`:
				return <GeneralSettings setAlert={setAlert} displayCustomError={displayCustomError} />;
			case `/restaurants/${id}/settings/branch`:
			case `/suppliers/${id}/settings/branch`:
				return (
					<BranchSettings
						setAlert={setAlert}
						displayCustomError={displayCustomError}
						setSearchValue={setSearchValue}
						searchValue={searchValue}
						rows={rows}
						setRows={setRows}
						filteredRows={filteredRows}
						setFilteredRows={setFilteredRows}
					/>
				);
			case `/restaurants/${id}/settings/teams&permissions`:
			case `/suppliers/${id}/settings/teams&permissions`:
				return (
					<TeamsAndPermission
						setAlert={setAlert}
						displayCustomError={displayCustomError}
						view={view}
						setView={setView}
						setSearchValue={setSearchValue}
						searchValue={searchValue}
					/>
				);
			case `/restaurants/${id}/settings/integrations`:
			case `/suppliers/${id}/settings/integrations`:
				return (
					<Integrations
						view={integrationsView}
						setView={setIntegrationsView}
						setAlert={setAlert}
						displayCustomError={displayCustomError}
					/>
				);
			case `/suppliers/${id}/settings/tallyApproval`:
				return (
					<ApprovalList
						setAlert={setAlert}
						displayCustomError={displayCustomError}
						rows={rows}
						setRows={setRows}
						filteredRows={filteredRows}
						setFilteredRows={setFilteredRows}
					/>
				);
			default:
				return <GeneralSettings setAlert={setAlert} displayCustomError={displayCustomError} />;
		}
	};

	return (
		<StyledContainer style={pageContainerStyles}>
			{!isMobileWidth ? (
				<PageHeader
					title={pageTitle(location?.pathname)}
					activeIcon={activeBackIcon(location?.pathname)}
					handleArrowBack={handleArrowBack}
				/>
			) : null}
			{isMobileWidth
				? settingsMobileRoutes(location?.pathname, id, userId)
				: settingsRoutes(location?.pathname, id, userId)}
			{alert?.message !== '' ? <CustomAlert alert={alert} setAlert={setAlert} /> : null}
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				open={isErrorVisible}
				autoHideDuration={6000}
				onClose={handleErrorClose}
			>
				<Alert onClose={handleErrorClose} severity="error" elevation={4}>
					{errorMessage}
				</Alert>
			</Snackbar>
		</StyledContainer>
	);
};

export default Settings;
